import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription, interval } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AuthUser, AuthResponse, RefreshResponse } from '@model';
import { AuthStateService } from './auth-state.service';

const USER_PROFILE_KEY = 'optima_user_profile';

const REFRESH_CHECK_INTERVAL = 60 * 1000;
const REFRESH_BEFORE_EXPIRY = 5 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private readonly apiUrl = '/api';
  private readonly authenticatedSubject = new BehaviorSubject<boolean>(false);
  private readonly userSubject = new BehaviorSubject<AuthUser | null>(null);
  private refreshSubscription: Subscription | null = null;
  private isRefreshing = false;
  private isAuthCheckInProgress = false;
  private expiresAt = 0;

  public authenticated$ = this.authenticatedSubject.asObservable();
  public user$ = this.userSubject.asObservable();
  public authInitialized$ = this.authStateService.authInitialized$;

  constructor(
    private readonly http: HttpClient,
    private readonly authStateService: AuthStateService
  ) {
    this.initializeAuth();
  }

  ngOnDestroy(): void {
    this.stopRefreshTimer();
  }

  private initializeAuth(): void {
    const searchParams = new URLSearchParams(globalThis.location.search);
    const authError = searchParams.get('auth_error');
    const logoutComplete = searchParams.get('logout_complete');
    const silentFailed = searchParams.get('silent_failed');
    
    // Clear redirect flag on any page load (successful navigation means we're done redirecting)
    sessionStorage.removeItem('optima_redirecting_to_login');
    sessionStorage.removeItem('optima_forbidden_redirect');
    sessionStorage.removeItem('optima_api_auth_error');
    
    // Handle logout completion - clear everything and stay logged out
    if (logoutComplete === 'true') {
      this.stopRefreshTimer();
      this.clearTokens();
      sessionStorage.removeItem('optima_silent_tried');
      sessionStorage.removeItem('optima_auth_error');
      this.authenticatedSubject.next(false);
      this.userSubject.next(null);
      
      const cleanUrl = globalThis.location.origin + globalThis.location.pathname;
      globalThis.history.replaceState({}, document.title, cleanUrl);
      
      // Don't redirect to login - user explicitly logged out
      // They will see the app in unauthenticated state and can click login if they want
      return;
    }
    
    // Handle silent SSO failure - now do interactive login
    if (silentFailed === 'true') {
      sessionStorage.setItem('optima_silent_tried', 'true');
      
      const cleanUrl = globalThis.location.origin + globalThis.location.pathname;
      globalThis.history.replaceState({}, document.title, cleanUrl);
      
      globalThis.location.replace('/api/auth/login');
      return;
    }
    
    // Handle auth error - DO NOT redirect to login again (prevents loop)
    if (authError) {
      console.error('❌ [AUTH] Authentication error:', authError);
      this.stopRefreshTimer();
      this.clearTokens();
      sessionStorage.removeItem('optima_silent_tried');
      this.authenticatedSubject.next(false);
      this.userSubject.next(null);
      
      // Clean URL but STAY on page - show error to user
      const cleanUrl = globalThis.location.origin + globalThis.location.pathname;
      globalThis.history.replaceState({}, document.title, cleanUrl);
      
      // Store error for display
      sessionStorage.setItem('optima_auth_error', authError);
      
      // DO NOT redirect to login - this causes infinite loop
      // Instead, redirect to a forbidden/error page or stay put
      console.error('🛑 [AUTH] Stopping here - auth error detected:', authError);
      globalThis.location.replace('/forbidden/index.html');
      return;
    }
    
    const authSuccess = searchParams.get('auth_success');

    if (authSuccess === 'true') {
      sessionStorage.removeItem('optima_silent_tried');

      const cleanUrl = globalThis.location.origin + globalThis.location.pathname;
      globalThis.history.replaceState({}, document.title, cleanUrl);

      this.checkAuthStatus().subscribe(authenticated => {
        if (authenticated) this.startRefreshTimer();
      });
    } else {
      this.checkAuthStatus().subscribe(authenticated => {
        if (authenticated) {
          this.startRefreshTimer();
        } else {
          const silentTried = sessionStorage.getItem('optima_silent_tried');
          if (!silentTried) {
            sessionStorage.setItem('optima_silent_tried', 'true');
            globalThis.location.replace('/api/auth/silent');
          } else {
            this.authenticatedSubject.next(false);
            this.userSubject.next(null);
          }
        }
      });
    }
  }

  /**
   * Clear session metadata (tokens are httpOnly cookies managed by the BFF)
   */
  clearTokens(): void {
    this.expiresAt = 0;
    this.clearUserProfileCache();
  }

  /**
   * Check if the current session has expired (based on in-memory expiry time)
   */
  isTokenExpired(): boolean {
    if (!this.expiresAt) return true;
    return Date.now() >= this.expiresAt;
  }

  /**
   * Get time until session expires in milliseconds
   */
  getTimeUntilExpiry(): number {
    if (!this.expiresAt) return 0;
    return Math.max(0, this.expiresAt - Date.now());
  }

  /**
   * Check if token needs refresh (expires within REFRESH_BEFORE_EXPIRY)
   */
  private needsRefresh(): boolean {
    const timeUntilExpiry = this.getTimeUntilExpiry();
    return timeUntilExpiry > 0 && timeUntilExpiry <= REFRESH_BEFORE_EXPIRY;
  }

  /**
   * Start the auto-refresh timer
   */
  private startRefreshTimer(): void {
    this.stopRefreshTimer();
        
    this.refreshSubscription = interval(REFRESH_CHECK_INTERVAL).subscribe(() => {
      if (this.isTokenExpired() || this.needsRefresh()) {
        this.refreshAccessToken();
      }
    });
  }

  /**
   * Stop the auto-refresh timer
   */
  private stopRefreshTimer(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = null;
    }
  }

  /**
   * Refresh access token using refresh token
   */
  private refreshAccessToken(): void {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;

    // Refresh token is in an httpOnly cookie; withCredentials sends it automatically.
    // No body needed—the server reads the cookie directly.
    this.http
      .post<{ success: boolean; expiresIn: number }>(`${this.apiUrl}/auth/refresh`, {})
      .pipe(
        tap(response => {
          if (response?.expiresIn) {
            this.expiresAt = Date.now() + response.expiresIn * 1000;
          }
        }),
        catchError(error => {
          console.error('💥 [AUTH] Refresh failed:', error.status);
          if (error.status === 401 || error.status === 403) {
            this.stopRefreshTimer();
          }
          return of(null);
        }),
      )
      .subscribe(() => {
        this.isRefreshing = false;
      });
  }

  /**
   * Initiate login - redirects to Azure AD
   */
  login(): void {
    globalThis.location.href = `${this.apiUrl}/auth/login`;
  }

  /**
   * Logout user - clears all state and redirects to Azure AD logout
   */
  logout(): void {    
    // Stop any refresh timers FIRST
    this.stopRefreshTimer();
    
    // Clear ALL state
    this.authenticatedSubject.next(false);
    this.userSubject.next(null);
    
    // Clear metadata (tokens are httpOnly cookies cleared by the BFF /auth/logout endpoint)
    this.expiresAt = 0;
    this.clearUserProfileCache();
    
    // Clear flow control flags from sessionStorage
    sessionStorage.removeItem('optima_silent_tried');
    sessionStorage.removeItem('optima_redirecting_to_login');
    sessionStorage.removeItem('optima_forbidden_redirect');
    sessionStorage.removeItem('optima_api_auth_error');
    sessionStorage.removeItem('optima_auth_error');
    
    // Reset auth state tracking
    this.authStateService.resetAuthState();
        
    // Redirect to logout endpoint
    globalThis.location.replace(`${this.apiUrl}/auth/logout`);
  }

  /**
   * Check authentication status
   */
  checkAuthStatus(): Observable<boolean> {
    // If we're already redirecting to forbidden page, don't process any more auth checks
    if (sessionStorage.getItem('optima_forbidden_redirect') === 'true') {
      return of(false);
    }

    // If auth check is already in progress, wait for it
    if (this.isAuthCheckInProgress) {
      return this.authInitialized$;
    }

    // Mark auth check as in progress
    this.isAuthCheckInProgress = true;

    // Get browser timezone and add as header
    const browserTimezone = this.getBrowserTimezone();
    const headers = new HttpHeaders({
      'X-Browser-Timezone': browserTimezone
    });

    // Validate token with backend and get enriched user data
    return this.http.get<AuthResponse>(`${this.apiUrl}/auth/me`, { headers })
      .pipe(
        map(response => {
          this.isAuthCheckInProgress = false;
          
          if (response.authenticated && response.user) {
            this.authenticatedSubject.next(true);
            this.userSubject.next(response.user);
            this.authStateService.setAuthInitialized(true);
            
            // Cache enriched user profile
            this.cacheUserProfile(response.user);
            
            if (response.expiresAt) {
              // expiresAt comes as an ISO string from the BFF → convert to ms timestamp
              this.expiresAt = new Date(response.expiresAt).getTime();
            }
            
            return true;
          } else {
            this.authenticatedSubject.next(false);
            this.userSubject.next(null);
            this.authStateService.setAuthInitialized(false);
            return false;
          }
        }),
        catchError(error => {
          this.isAuthCheckInProgress = false;          
          if (error.status === 401) {
            // Check if it's a group authorization error (not authorized for this app)
            if (error.error?.code === 'UNAUTHORIZED' && error.error?.error?.includes('not authorized')) {
              console.error('❌ [AUTH] User not authorized - redirecting to forbidden page');
              
              // Set flag to prevent any retry attempts
              sessionStorage.setItem('optima_forbidden_redirect', 'true');
              
              this.clearTokens();
              this.authenticatedSubject.next(false);
              this.userSubject.next(null);
              this.authStateService.setAuthInitialized(false);
              
              // Redirect to forbidden page with type=unauthorized to show retry button
              setTimeout(() => {
                globalThis.location.replace('/forbidden/index.html?type=unauthorized');
              }, 0);
              
              return of(false);
            }
            
            // Regular 401 - just clear tokens
            this.clearTokens();
          }
          
          this.authenticatedSubject.next(false);
          this.userSubject.next(null);
          this.authStateService.setAuthInitialized(false);
          return of(false);
        })
      );
  }

  isAuthenticated(): boolean {
    return this.authenticatedSubject.value;
  }

  getCurrentUser(): AuthUser | null {
    return this.userSubject.value;
  }

  /**
   * Force re-authentication - clears everything and redirects to login
   */
  forceReauth(): void {    
    // Check if we're already redirecting to prevent loops
    if (sessionStorage.getItem('optima_redirecting_to_login') === 'true') {
      return;
    }
    
    this.stopRefreshTimer();
    this.authenticatedSubject.next(false);
    this.userSubject.next(null);
    
    // Set flag and clear metadata (httpOnly cookies are cleared by the BFF)
    sessionStorage.setItem('optima_redirecting_to_login', 'true');
    this.expiresAt = 0;
    
    // Use replace to prevent back button issues
    globalThis.location.replace('/api/auth/login');
  }

  /**
   * Get browser timezone using Intl API
   */
  private getBrowserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  }

  /**
   * Cache user profile in localStorage
   */
  private cacheUserProfile(user: AuthUser): void {
    try {
      localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('💥 [AUTH] Error caching user profile:', error);
    }
  }

  /**
   * Get cached user profile from localStorage
   */
  getUserProfileFromCache(): AuthUser | null {
    try {
      const cached = localStorage.getItem(USER_PROFILE_KEY);
      if (!cached) return null;
      const parsed = JSON.parse(cached);
      // Basic shape validation — localStorage can be manipulated by the user
      if (!parsed || typeof parsed !== 'object' || typeof parsed['email'] !== 'string') {
        this.clearUserProfileCache();
        return null;
      }
      return parsed as AuthUser;
    } catch (error) {
      console.error('💥 [AUTH] Error reading cached user profile:', error);
      this.clearUserProfileCache();
      return null;
    }
  }

  /**
   * Clear cached user profile from localStorage
   */
  private clearUserProfileCache(): void {
    localStorage.removeItem(USER_PROFILE_KEY);
  }
}